'use strict';
/**
 * ProvenanceNodeRenderers — thin subclasses of pict-section-objecteditor's per-type node renderers
 * that STAMP provenance fields onto the node descriptor before the standard render runs. Because
 * compileMacros() (PictView-ObjectEditor-Node.js) parses the view's MacroTemplates against the node,
 * and ProvenanceObjectEditor's macros reference {~D:Record.ProvenanceBadgeHTML~} / ProvenanceRowClass,
 * stamping those fields here is all that's needed — no template overrides, and it regenerates on every
 * renderTree() so it survives the editor's frequent re-renders.
 */

const libNodeString = require('pict-section-objecteditor/source/views/PictView-ObjectEditor-NodeString.js');
const libNodeNumber = require('pict-section-objecteditor/source/views/PictView-ObjectEditor-NodeNumber.js');
const libNodeBoolean = require('pict-section-objecteditor/source/views/PictView-ObjectEditor-NodeBoolean.js');
const libNodeNull = require('pict-section-objecteditor/source/views/PictView-ObjectEditor-NodeNull.js');
const libNodeObject = require('pict-section-objecteditor/source/views/PictView-ObjectEditor-NodeObject.js');
const libNodeArray = require('pict-section-objecteditor/source/views/PictView-ObjectEditor-NodeArray.js');

const libProvenance = require('../Config-Provenance.js');

// Short pill text + full tooltip per scope.
const SCOPE_SHORT = { Global: 'Default', Customer: 'Customer', Contract: 'Contract', Project: 'Project', User: 'You' };
const SCOPE_FULL = { Global: 'Platform Default', Customer: 'Customer', Contract: 'Contract', Project: 'Project', User: 'My Settings' };

/**
 * Resolve a dot-address (e.g. 'AppData.ConfigViewer.ProvenanceMap') from the fable/pict root.
 * @param {object} pFable
 * @param {string} pAddress
 * @return {*}
 */
function resolveAddress(pFable, pAddress)
{
	if (!pAddress)
	{
		return null;
	}
	let tmpCurrent = pFable;
	const tmpParts = String(pAddress).split('.');
	for (let i = 0; i < tmpParts.length; i++)
	{
		if (tmpCurrent === null || tmpCurrent === undefined)
		{
			return null;
		}
		tmpCurrent = tmpCurrent[tmpParts[i]];
	}
	return tmpCurrent;
}

/**
 * Escape for an HTML attribute (the title).
 * @param {string} pString
 * @return {string}
 */
function escapeAttribute(pString)
{
	return String(pString).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Stamp pNode.ProvenanceRowClass + pNode.ProvenanceBadgeHTML from the provenance map at the view's
 * ProvenanceDataAddress. Container nodes (and the CustomProperties flat-blob mode, where the map is
 * empty) get no badge.
 * @param {object} pView the ProvenanceObjectEditor view
 * @param {object} pNode the node descriptor
 */
function applyProvenanceToNode(pView, pNode)
{
	const tmpAddress = (pView.options && pView.options.ProvenanceDataAddress) || 'AppData.ConfigViewer.ProvenanceMap';
	const tmpMap = resolveAddress(pView.fable, tmpAddress) || {};
	const tmpProv = libProvenance.lookupProvenance(tmpMap, pNode.Path);
	if (!tmpProv)
	{
		pNode.ProvenanceRowClass = '';
		pNode.ProvenanceBadgeHTML = '';
		return;
	}
	const tmpScope = tmpProv.Scope;
	const tmpClass = 'cfg-prov-' + String(tmpScope).toLowerCase();
	pNode.ProvenanceRowClass = tmpClass;
	pNode.ProvenanceBadgeHTML = '<span class="cfg-prov-badge ' + tmpClass + '" title="Set at '
		+ escapeAttribute(SCOPE_FULL[tmpScope] || tmpScope) + '">' + escapeAttribute(SCOPE_SHORT[tmpScope] || tmpScope) + '</span>';
}

/**
 * Build a provenance-aware subclass of a base node renderer.
 * @param {Function} pBaseClass
 * @return {Function}
 */
function makeProvenanceRenderer(pBaseClass)
{
	return class extends pBaseClass
	{
		renderNodeHTML(pNode, pValue, pOptions)
		{
			applyProvenanceToNode(this._ObjectEditorView, pNode);
			return super.renderNodeHTML(pNode, pValue, pOptions);
		}
	};
}

module.exports =
{
	applyProvenanceToNode,
	// Service-type name -> subclass, plus the node-type key it serves.
	Renderers:
	[
		{ TypeKey: 'string', ServiceType: 'PictViewProvNodeString', Class: makeProvenanceRenderer(libNodeString) },
		{ TypeKey: 'number', ServiceType: 'PictViewProvNodeNumber', Class: makeProvenanceRenderer(libNodeNumber) },
		{ TypeKey: 'boolean', ServiceType: 'PictViewProvNodeBoolean', Class: makeProvenanceRenderer(libNodeBoolean) },
		{ TypeKey: 'null', ServiceType: 'PictViewProvNodeNull', Class: makeProvenanceRenderer(libNodeNull) },
		{ TypeKey: 'object', ServiceType: 'PictViewProvNodeObject', Class: makeProvenanceRenderer(libNodeObject) },
		{ TypeKey: 'array', ServiceType: 'PictViewProvNodeArray', Class: makeProvenanceRenderer(libNodeArray) },
	],
};
